import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import { useWatch } from "antd/lib/form/Form";
import moment, { Moment } from "moment";
import { useTranslation } from "react-i18next";

import { AmountInput } from "@/modules/amount-input";

import { TaxServiceName } from "~/api/graphql/schema.generated";
import { useRecurringFeeQuery } from "~/api/graphql/queries/recurringFee/recurringFee.generated";
import { useUpsertRecurringFeeMutation } from "~/api/graphql/mutations/recurringFee/upsertRecurringFee.generated";
import { useDeleteRecurringFeeMutation } from "~/api/graphql/mutations/recurringFee/deleteRecurringFee.generated";
import {
  OTHER_RECURRING_FEE_TAX_SERVICES,
  RECURRING_FEE_DEFAULT_AMOUNTS_IN_EUROS,
  RECURRING_FEE_RHYTHMS_IN_MONTHS,
} from "./constants";
import {
  centsToEuros,
  eurosToCents,
  showGraphQlErrorNotification,
  showSuccessMessage,
} from "~/utils";
import { ExportSwitch } from "./ExportSwitch";
import { buildInvoicePreview } from "./invoicePreview";
import { formatPeriod, getSkippedPeriods } from "./skippedPeriods";

const { Title, Text } = Typography;

const DATE_FORMAT = "YYYY-MM-DD";
const DISPLAY_DATE_FORMAT = "DD.MM.YYYY";

// The kind picker: the three named products, plus "Sonstige".
export const OTHER_KIND = "OTHER";
const NAMED_KINDS = [
  TaxServiceName.RECURRING_TAX_SERVICE,
  TaxServiceName.RECURRING_TAX_SERVICE_COMPACT,
  TaxServiceName.RECURRING_BOOKKEEPING_SERVICE,
];

const BILLING_DAYS = Array.from({ length: 28 }, (_, index) => index + 1);
const MONTHS = Array.from({ length: 12 }, (_, index) => index + 1);

export interface RecurringFeeFormValues {
  kind?: TaxServiceName | typeof OTHER_KIND;
  otherServiceName?: TaxServiceName;
  customLabel?: string | null;
  customDescription?: string | null;
  amountInEuros?: number | null;
  rhythmInMonths: number;
  billingDay?: number;
  anchorMonth?: number | null;
  validFrom?: Moment | null;
  validUntil?: Moment | null;
  includeInExport: boolean;
}

const isOther = (name?: TaxServiceName | null) =>
  !!name && OTHER_RECURRING_FEE_TAX_SERVICES.includes(name);

/*
 * The customer's recurring fee (SMT-1366), always visible on the
 * Leistungstracker page. It is a property of the customer, not a service
 * recorded per period: the job turns it into one row per due period, and a
 * past period is recorded by hand under "+ Leistung erfassen".
 */
export const RecurringFeeBlock = ({ email }: { email: string }) => {
  const { t } = useTranslation("ServiceTracker");
  const tr = (key: string, options?: Record<string, string>): string =>
    String(t(`serviceTracker.recurringFee.${key}`, options ?? {}));

  const [form] = Form.useForm<RecurringFeeFormValues>();
  const kind = useWatch("kind", form);
  const rhythmInMonths = useWatch("rhythmInMonths", form);
  const billingDay = useWatch("billingDay", form);
  const anchorMonth = useWatch("anchorMonth", form);
  const validFrom = useWatch("validFrom", form);
  const otherServiceName = useWatch("otherServiceName", form);
  const customLabel = useWatch("customLabel", form);
  const customDescription = useWatch("customDescription", form);
  const validUntil = useWatch("validUntil", form);

  const { data, loading, refetch } = useRecurringFeeQuery({
    variables: { email },
    fetchPolicy: "network-only",
  });
  const fee = data?.recurringFee;
  const [upsertRecurringFee, { loading: isSaving }] =
    useUpsertRecurringFeeMutation();
  const [deleteRecurringFee, { loading: isDeleting }] =
    useDeleteRecurringFeeMutation();

  const [isOpen, setIsOpen] = useState(false);

  const fillForm = useCallback(() => {
    form.resetFields();
    if (!fee) {
      return;
    }
    form.setFieldsValue({
      kind: isOther(fee.serviceName) ? OTHER_KIND : fee.serviceName,
      otherServiceName: isOther(fee.serviceName) ? fee.serviceName : undefined,
      customLabel: fee.customLabel,
      customDescription: fee.customDescription,
      amountInEuros: centsToEuros(fee.amountInCents),
      rhythmInMonths: fee.rhythmInMonths,
      billingDay: fee.billingDay,
      anchorMonth: fee.anchorMonth,
      validFrom: moment(fee.validFrom, DATE_FORMAT),
      validUntil: fee.validUntil ? moment(fee.validUntil, DATE_FORMAT) : null,
      includeInExport: fee.includeInExport,
    });
  }, [fee, form]);

  useEffect(fillForm, [fillForm]);

  const open = () => {
    fillForm();
    setIsOpen(true);
  };

  const kindOptions = useMemo(
    () => [
      ...NAMED_KINDS.map((name) => ({
        value: name,
        label: tr(`kinds.${name}`),
      })),
      { value: OTHER_KIND, label: tr(`kinds.${OTHER_KIND}`) },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const handleKindChange = (value: RecurringFeeFormValues["kind"]) => {
    const defaultAmount =
      value && value !== OTHER_KIND
        ? RECURRING_FEE_DEFAULT_AMOUNTS_IN_EUROS[value]
        : undefined;
    const currentAmount = form.getFieldValue("amountInEuros");
    const currentIsADefault = Object.values(
      RECURRING_FEE_DEFAULT_AMOUNTS_IN_EUROS
    ).includes(currentAmount);
    if (defaultAmount) {
      form.setFieldsValue({ amountInEuros: defaultAmount });
    } else if (currentIsADefault) {
      // Sonstige has no default: do not carry over another product's price.
      form.setFieldsValue({ amountInEuros: null });
    }
  };

  // The anchor follows the customer's start month until it is picked by hand.
  const handleValidFromChange = (value: Moment | null) => {
    if (value && !form.isFieldTouched("anchorMonth")) {
      form.setFieldsValue({ anchorMonth: value.month() + 1 });
    }
  };

  const handleFinish = async (values: RecurringFeeFormValues) => {
    const serviceName =
      values.kind === OTHER_KIND ? values.otherServiceName : values.kind;
    if (!serviceName || !values.validFrom || !values.amountInEuros) {
      return;
    }
    try {
      await upsertRecurringFee({
        variables: {
          email,
          payload: {
            serviceName,
            customLabel: values.customLabel || null,
            customDescription: values.customDescription || null,
            amountInCents: eurosToCents(values.amountInEuros),
            rhythmInMonths: values.rhythmInMonths,
            billingDay: values.billingDay!,
            anchorMonth:
              values.rhythmInMonths > 1 ? values.anchorMonth ?? null : null,
            validFrom: values.validFrom.format(DATE_FORMAT),
            validUntil: values.validUntil
              ? values.validUntil.format(DATE_FORMAT)
              : null,
            includeInExport: values.includeInExport,
          },
        },
      });
      showSuccessMessage(tr("saved"));
      setIsOpen(false);
      await refetch();
    } catch (error) {
      showGraphQlErrorNotification("Saving recurring fee", error);
    }
  };

  /*
   * For the customer who backs out before anything was billed. The backend
   * refuses once a row exists; that fee is ended with Läuft bis instead.
   */
  const handleDelete = async () => {
    try {
      await deleteRecurringFee({ variables: { email } });
      showSuccessMessage(tr("deleted"));
      setIsOpen(false);
      await refetch();
    } catch (error) {
      showGraphQlErrorNotification(tr("deleteFailed"), error);
    }
  };

  const required = [{ required: true, message: tr("required") }];

  /*
   * Periods the job will not bill on its own (Gilt ab after the Stichtag, or
   * a new fee whose Stichtage already passed). Deliberate, but easy to miss,
   * so it is said while the fee is being set.
   */
  const skipped =
    validFrom && billingDay && rhythmInMonths
      ? getSkippedPeriods(
          {
            rhythmInMonths,
            billingDay,
            anchorMonth: anchorMonth ?? validFrom.month() + 1,
            validFrom: validFrom.format(DATE_FORMAT),
          },
          moment().format(DATE_FORMAT),
          !fee
        )
      : null;
  const skippedPeriods = skipped
    ? [
        ...(skipped.beforeValidFrom ? [skipped.beforeValidFrom] : []),
        ...skipped.beforeToday,
      ].map((period) => formatPeriod(period, rhythmInMonths))
    : [];

  // One line instead of the whole form: the fee is set rarely and read often.
  const summary = fee
    ? [
        isOther(fee.serviceName)
          ? `${fee.customLabel} (${tr(`stbvvTitles.${fee.serviceName}`)})`
          : tr(`kinds.${fee.serviceName}`),
        tr("summaryAmount", {
          amount: centsToEuros(fee.amountInCents).toLocaleString("de-DE", {
            minimumFractionDigits: 2,
          }),
        }),
        tr("summaryRhythm", {
          rhythm: tr(`rhythms.${fee.rhythmInMonths}`),
          day: String(fee.billingDay),
        }),
        fee.validUntil
          ? tr("summaryValidRange", {
              from: moment(fee.validFrom).format(DISPLAY_DATE_FORMAT),
              until: moment(fee.validUntil).format(DISPLAY_DATE_FORMAT),
            })
          : tr("summaryValidFrom", {
              from: moment(fee.validFrom).format(DISPLAY_DATE_FORMAT),
            }),
        ...(fee.billedThroughMonth
          ? [
              tr("billedThrough", {
                period: moment(fee.billedThroughMonth, DATE_FORMAT).format(
                  "MM/YYYY"
                ),
              }),
            ]
          : []),
        fee.includeInExport ? tr("summaryExportOn") : tr("summaryExportOff"),
      ].join(" · ")
    : null;

  // What the customer will read, built as the job builds it.
  // Before a Sonstige title is picked, preview with any Sonstige name.
  const previewServiceName =
    kind === OTHER_KIND
      ? otherServiceName ?? TaxServiceName.RECURRING_OTHER_ADVANCE
      : kind;
  const preview =
    previewServiceName && validFrom && billingDay && rhythmInMonths
      ? buildInvoicePreview(
          {
            serviceName: previewServiceName,
            rhythmInMonths,
            billingDay,
            anchorMonth: anchorMonth ?? validFrom.month() + 1,
            validFrom: validFrom.format(DATE_FORMAT),
            customLabel,
            customDescription,
            billedThroughMonth: fee?.billedThroughMonth,
            validUntil: validUntil ? validUntil.format(DATE_FORMAT) : null,
          },
          moment().format(DATE_FORMAT)
        )
      : null;

  return (
    <div data-test="recurringFeeBlock" style={{ marginBottom: 24 }}>
      <Title level={4}>{tr("title")}</Title>
      <Space wrap>
        {fee ? (
          <Text data-test="recurringFeeSummary">{summary}</Text>
        ) : (
          !loading && (
            <Text type="secondary" data-test="recurringFeeNotSet">
              {tr("notSet")}
            </Text>
          )
        )}
        <Button onClick={open} data-test="recurringFeeOpen">
          {fee ? tr("edit") : tr("create")}
        </Button>
      </Space>
      <Modal
        title={tr("title")}
        visible={isOpen}
        onCancel={() => setIsOpen(false)}
        width={720}
        forceRender
        footer={[
          // Only while it can succeed: the backend refuses once a row exists.
          fee && !fee.hasGeneratedRows && (
            <Popconfirm
              key="delete"
              title={tr("deleteConfirm")}
              okText={tr("deleteOk")}
              cancelText={tr("deleteCancel")}
              onConfirm={handleDelete}
            >
              <Button
                danger
                loading={isDeleting}
                style={{ float: "left" }}
                data-test="recurringFeeDelete"
              >
                {tr("delete")}
              </Button>
            </Popconfirm>
          ),
          <Button key="cancel" onClick={() => setIsOpen(false)}>
            {tr("cancel")}
          </Button>,
          <Button
            key="save"
            type="primary"
            htmlType="submit"
            form="recurringFeeForm"
            loading={isSaving}
            data-test="recurringFeeSave"
          >
            {tr("save")}
          </Button>,
        ]}
      >
        <Form
          form={form}
          id="recurringFeeForm"
          layout="vertical"
          requiredMark={false}
          onFinish={handleFinish}
          initialValues={{ rhythmInMonths: 1, includeInExport: false }}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="kind" label={tr("kindLabel")} rules={required}>
                <Select
                  options={kindOptions}
                  onChange={handleKindChange}
                  data-test="recurringFeeKind"
                />
              </Form.Item>
            </Col>
            {kind === OTHER_KIND && (
              <Col span={12}>
                <Form.Item
                  name="otherServiceName"
                  label={tr("stbvvTitleLabel")}
                  rules={required}
                >
                  <Select
                    data-test="recurringFeeStbvvTitle"
                    options={OTHER_RECURRING_FEE_TAX_SERVICES.map((name) => ({
                      value: name,
                      label: tr(`stbvvTitles.${name}`),
                    }))}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
          {kind === OTHER_KIND && (
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="customLabel"
                  label={tr("customLabelLabel")}
                  extra={tr("customLabelHelp")}
                  rules={[{ ...required[0], whitespace: true }]}
                >
                  <Input
                    maxLength={100}
                    placeholder={tr("customLabelPlaceholder")}
                    data-test="recurringFeeCustomLabel"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="customDescription"
                  label={tr("customDescriptionLabel")}
                  extra={tr("customDescriptionHelp")}
                >
                  <Input
                    maxLength={120}
                    placeholder={tr("customDescriptionPlaceholder")}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="amountInEuros"
                label={tr("amountLabel")}
                rules={[
                  {
                    validator: (_, value?: number | null) =>
                      value && value > 0
                        ? Promise.resolve()
                        : Promise.reject(new Error(tr("amountError"))),
                  },
                ]}
              >
                <AmountInput
                  addonAfter="€"
                  style={{ width: "100%" }}
                  data-test="recurringFeeAmount"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="rhythmInMonths"
                label={tr("rhythmLabel")}
                rules={required}
              >
                <Select
                  options={RECURRING_FEE_RHYTHMS_IN_MONTHS.map((months) => ({
                    value: months,
                    label: tr(`rhythms.${months}`),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="billingDay"
                label={tr("billingDayLabel")}
                rules={required}
              >
                <Select
                  data-test="recurringFeeBillingDay"
                  options={BILLING_DAYS.map((day) => ({
                    value: day,
                    label: `${day}.`,
                  }))}
                />
              </Form.Item>
            </Col>
            {rhythmInMonths > 1 && (
              <Col span={8}>
                <Form.Item
                  name="anchorMonth"
                  label={tr("anchorMonthLabel")}
                  rules={required}
                >
                  <Select
                    data-test="recurringFeeAnchorMonth"
                    options={MONTHS.map((month) => ({
                      value: month,
                      label: moment()
                        .month(month - 1)
                        .format("MMMM"),
                    }))}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
          <Row gutter={12} align="bottom">
            <Col span={8}>
              <Form.Item
                name="validFrom"
                label={tr("validFromLabel")}
                rules={required}
              >
                <DatePicker
                  format={DISPLAY_DATE_FORMAT}
                  style={{ width: "100%" }}
                  onChange={handleValidFromChange}
                  data-test="recurringFeeValidFrom"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="validUntil" label={tr("validUntilLabel")}>
                <DatePicker
                  format={DISPLAY_DATE_FORMAT}
                  style={{ width: "100%" }}
                  placeholder={tr("validUntilPlaceholder")}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="includeInExport"
                label={tr("includeInExportLabel")}
                valuePropName="checked"
                /*
                 * Decided 28.09.2026 on Shreyans' review: the switch applies
                 * to rows generated from now on. Rows that exist keep the
                 * value they were generated with, so the operator is told.
                 */
                extra={
                  fee?.hasGeneratedRows ? (
                    <span data-test="recurringFeeExportFutureOnly">
                      {tr("includeInExportFutureOnly")}
                    </span>
                  ) : null
                }
              >
                <ExportSwitch data-test="recurringFeeIncludeInExport" />
              </Form.Item>
            </Col>
          </Row>
          {preview && (
            <div
              data-test="recurringFeePreview"
              style={{
                marginBottom: 12,
                padding: 12,
                backgroundColor: "#f5f5f5",
                borderRadius: 4,
              }}
            >
              <Text strong>{tr("previewTitle")}</Text>
              <div style={{ marginTop: 6 }}>
                <Text type="secondary">{tr("previewTitleLabel")} </Text>
                <Text>
                  {kind === OTHER_KIND && !otherServiceName
                    ? tr("previewPickTitle")
                    : preview.title}
                </Text>
              </div>
              <div>
                <Text type="secondary">{tr("previewTextLabel")} </Text>
                <Text data-test="recurringFeePreviewText">{preview.text}</Text>
              </div>
            </div>
          )}
          {!preview && previewServiceName && validFrom && billingDay && (
            <Text
              type="secondary"
              style={{ display: "block", marginBottom: 12 }}
              data-test="recurringFeeNoFurtherPeriod"
            >
              {tr("previewNoFurtherPeriod")}
            </Text>
          )}
          {skippedPeriods.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              data-test="recurringFeeSkippedPeriods"
              message={tr("skippedPeriods", {
                periods: skippedPeriods.join(", "),
              })}
            />
          )}
        </Form>
      </Modal>
    </div>
  );
};
